/**
 * Helpers for full-bleed hero / slideshow panels (~700px+ tall, retina cover).
 * Small Base-tab imports (e.g. Facebook thumbs) look fine in grids but pixelate when stretched.
 */

/** Minimum width for crisp cover on ~684px-wide panels at ~2x DPR. */
export const HERO_MIN_WIDTH = 1200;
/** Minimum height for tall hero/about columns. */
export const HERO_MIN_HEIGHT = 750;

/**
 * @param {{ dimensions?: { width?: number, height?: number } } | null} img
 * @returns {number}
 */
export function imagePixelArea(img) {
    const d = img?.dimensions || {};
    const w = Number(d.width) || 0;
    const h = Number(d.height) || 0;
    return w * h;
}

/**
 * @param {{ dimensions?: { width?: number, height?: number } } | null} img
 * @param {{ minWidth?: number, minHeight?: number, minArea?: number }} [opts]
 * @returns {boolean}
 */
export function isHeroSizedImage(img, opts = {}) {
    const minW = opts.minWidth ?? HERO_MIN_WIDTH;
    const minH = opts.minHeight ?? HERO_MIN_HEIGHT;
    const minArea = opts.minArea ?? 0;
    const d = img?.dimensions || {};
    const w = Number(d.width) || 0;
    const h = Number(d.height) || 0;
    if (!w || !h) return false;
    if (w >= minW && h >= minH) return true;
    if (minArea > 0 && w * h >= minArea) return true;
    return false;
}

/**
 * @param {Array<{ dimensions?: { width?: number, height?: number }, url?: string }>} images
 * @param {{ minWidth?: number, minHeight?: number, minArea?: number }} [opts]
 * @returns {typeof images}
 */
export function filterHeroSizedImages(images, opts = {}) {
    return (images || [])
        .filter((img) => isHeroSizedImage(img, opts))
        .sort((a, b) => imagePixelArea(b) - imagePixelArea(a));
}

/**
 * Prefer the largest hero-sized Base image; softer fallback when none meet hero thresholds.
 * @param {Array<{ dimensions?: object, url?: string }>} images
 * @returns {typeof images[0] | null}
 */
export function pickArtisanHeroImage(images) {
    if (!images?.length) return null;

    const heroPool = filterHeroSizedImages(images, {
        minWidth: 800,
        minHeight: 600
    });
    if (heroPool.length > 0) {
        const best = heroPool[0];
        if (imagePixelArea(best) >= 800 * 600) return best;
    }

    let best = images[0];
    let bestArea = imagePixelArea(best);
    for (let i = 1; i < images.length; i++) {
        const area = imagePixelArea(images[i]);
        if (area > bestArea) {
            bestArea = area;
            best = images[i];
        }
    }
    if (bestArea >= 800 * 600) return best;

    const today = new Date();
    const dayOfYear = Math.floor(
        (today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24
    );
    return images[(dayOfYear + 2) % images.length] || best;
}

/**
 * @param {Array<{ url?: string, filename?: string, imageUrl?: string, dimensions?: object }>} rawList
 * @returns {Array<{ url: string, dimensions: object | null }>}
 */
export function mapBackgroundEntries(rawList) {
    return (rawList || [])
        .filter((img) => img?.isActive !== false)
        .map((img) => {
            const raw = img.filename || img.url || img.imageUrl;
            const url =
                typeof raw === 'string' && raw.trim()
                    ? raw.startsWith('http') || raw.startsWith('/')
                        ? raw
                        : `/${raw}`
                    : '';
            return { url, dimensions: img.dimensions || null };
        })
        .filter((entry) => entry.url);
}
